import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useHttpClient } from "@/core/http/HttpClientContext";
import { useAppStore } from "@/core/store/useAppStore";
import { putToGCS } from "@/core/upload/putToGCS";
import {
  applySnapshot,
  hasActiveClips,
  mergeClips,
  type ClipSnapshot,
  type LocalUpload,
  type ServerClip,
  type SubmittedClip,
  type VoiceClipView,
} from "./clipList";
import { VOICE_POLL_MS } from "./limits";
import { resubmitClip, sendClip, uploadFailureStatus } from "./sendClip";
import { useBeforeUnloadWarning } from "./useBeforeUnloadWarning";
import { voiceService, type VoiceTarget } from "./voiceService";

/** The recording behind a local entry, kept until the server owns the clip. */
interface PendingUpload {
  /** The purpose and target it was recorded for, whatever the panel shows now. */
  purpose: string;
  target: VoiceTarget;
  scopeKey: string;
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
  /** Set once create succeeds. */
  clipId: string | null;
  /** Set once the PUT succeeds. A retry then only submits again. */
  putDone: boolean;
}

interface ScopedUpload {
  scopeKey: string;
  upload: LocalUpload;
}

interface ScopedSubmit extends SubmittedClip {
  scopeKey: string;
}

export interface UseVoiceClipsOptions {
  purpose: string;
  target: VoiceTarget;
  /** Called when a clip's result exists (e.g. refresh the notes list). */
  onCompleted: () => void;
}

const NO_CLIPS: ServerClip[] = [];
const TOAST_MS = 6000;
/** Never aborted: an upload outlives the panel, so the note still lands. */
const UPLOAD_SIGNAL = new AbortController().signal;

/** Numbers every open-list fetch, shared by hook instances on the same query. */
let listSeq = 0;

/** One purpose and target. Local entries and poll bookkeeping are kept per scope. */
function scopeKeyOf(purpose: string, target: VoiceTarget): string {
  return JSON.stringify([purpose, target]);
}

function clipsQueryKey(scopeKey: string) {
  return ["voiceClips", scopeKey];
}

/** After unmount the local row and its blob are gone, so the list offers no retry. */
function uploadFailureMessage(status: Exclude<LocalUpload["status"], "uploading">, mounted: boolean): string {
  if (status === "upload_rejected") return "This voice note can't be sent";
  return mounted
    ? "Voice note upload failed. Retry it from the list."
    : "Your voice note couldn't be uploaded. Please record it again.";
}

/** Upload, submit and poll voice clips for one purpose and target (TD-089). */
export function useVoiceClips({ purpose, target, onCompleted }: UseVoiceClipsOptions) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const addToast = useAppStore((s) => s.addToast);
  const scopeKey = scopeKeyOf(purpose, target);
  const queryKey = useMemo(() => clipsQueryKey(scopeKey), [scopeKey]);

  /** The last fetch applied for each scope: its clips are the previous list. */
  const appliedRef = useRef(new Map<string, ClipSnapshot>());
  const submittedRef = useRef<ScopedSubmit[]>([]);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const { data } = useQuery({
    queryKey,
    queryFn: async (): Promise<ClipSnapshot> => {
      listSeq += 1;
      const seq = listSeq;
      return { clips: await voiceService.listOpenClips(http, purpose, target), seq };
    },
    // Keep polling until every submitted clip has been judged, even when a
    // failed fetch left no active clip in the cache.
    refetchInterval: (query) =>
      hasActiveClips(query.state.data?.clips ?? NO_CLIPS) ||
      submittedRef.current.some((entry) => entry.scopeKey === scopeKey)
        ? VOICE_POLL_MS
        : false,
    staleTime: 0,
  });
  const server = data?.clips ?? NO_CLIPS;

  useEffect(() => {
    if (!data) return;
    const last = appliedRef.current.get(scopeKey);
    if (last && data.seq <= last.seq) return;
    appliedRef.current.set(scopeKey, data);
    const inScope = submittedRef.current.filter((entry) => entry.scopeKey === scopeKey);
    const applied = applySnapshot(last?.clips ?? NO_CLIPS, data, inScope);
    submittedRef.current = [
      ...submittedRef.current.filter((entry) => entry.scopeKey !== scopeKey),
      ...applied.submitted,
    ];
    if (applied.finished.length > 0) onCompletedRef.current();
  }, [data, scopeKey]);

  const [uploads, setUploads] = useState<ScopedUpload[]>([]);
  const pendingRef = useRef(new Map<string, PendingUpload>());
  const nextIdRef = useRef(0);
  /** False once the hosting component unmounts. Uploads in flight keep running. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const patchUpload = useCallback((localId: string, patch: Partial<LocalUpload>) => {
    setUploads((current) =>
      current.map((entry) =>
        entry.upload.localId === localId ? { ...entry, upload: { ...entry.upload, ...patch } } : entry,
      ),
    );
  }, []);

  const removeUpload = useCallback((localId: string) => {
    pendingRef.current.delete(localId);
    setUploads((current) => current.filter((entry) => entry.upload.localId !== localId));
  }, []);

  const refresh = useCallback(
    (key: string) => queryClient.invalidateQueries({ queryKey: clipsQueryKey(key) }),
    [queryClient],
  );

  /** Record the submit before refetching, so the refetch is the first fetch that judges it. */
  const markSubmitted = useCallback((clipId: string, key: string) => {
    submittedRef.current = [...submittedRef.current, { id: clipId, afterSeq: listSeq, scopeKey: key }];
  }, []);

  /** The server owns the clip now: drop the local entry and show the server row. */
  const handOver = useCallback(
    async (localId: string, clipId: string, key: string) => {
      markSubmitted(clipId, key);
      removeUpload(localId);
      await refresh(key);
    },
    [markSubmitted, removeUpload, refresh],
  );

  const failUpload = useCallback(
    (localId: string, status: Exclude<LocalUpload["status"], "uploading">) => {
      patchUpload(localId, { status });
      addToast({ type: "error", message: uploadFailureMessage(status, mountedRef.current), duration: TOAST_MS });
    },
    [patchUpload, addToast],
  );

  const runUpload = useCallback(
    async (localId: string) => {
      const pending = pendingRef.current.get(localId);
      if (!pending) return;

      const { clipId: uploadedClipId } = pending;
      if (uploadedClipId !== null && pending.putDone) {
        // Only submit failed. A new clip here could duplicate the note if the
        // lost submit had reached the server.
        patchUpload(localId, { status: "uploading", progress: 1 });
        const outcome = await resubmitClip((clipId) => voiceService.submitClip(http, clipId), uploadedClipId);
        if (outcome === "done") {
          await handOver(localId, uploadedClipId, pending.scopeKey);
        } else if (outcome === "gone") {
          removeUpload(localId);
          addToast({ type: "error", message: "That voice note no longer exists", duration: TOAST_MS });
        } else {
          failUpload(localId, "upload_failed");
        }
        return;
      }

      // A failed create or PUT retries as a new clip. The old uploading row never shows.
      pending.clipId = null;
      pending.putDone = false;
      patchUpload(localId, { status: "uploading", progress: 0 });
      let clipId: string;
      try {
        clipId = await sendClip(
          {
            purpose: pending.purpose,
            target: pending.target,
            blob: pending.blob,
            mimeType: pending.mimeType,
            durationSeconds: pending.durationSeconds,
          },
          {
            createClip: (request) => voiceService.createClip(http, request),
            putToGCS,
            submitClip: (id) => voiceService.submitClip(http, id),
            signal: UPLOAD_SIGNAL,
            onProgress: (fraction) => patchUpload(localId, { progress: fraction }),
            onCreated: (id) => {
              pending.clipId = id;
            },
            onUploaded: () => {
              pending.putDone = true;
            },
          },
        );
      } catch (error) {
        const stage = pending.clipId === null ? "create" : pending.putDone ? "submit" : "put";
        failUpload(localId, uploadFailureStatus(stage, error));
        return;
      }
      await handOver(localId, clipId, pending.scopeKey);
    },
    [http, patchUpload, removeUpload, handOver, failUpload, addToast],
  );

  const send = useCallback(
    (blob: Blob, mimeType: string, durationSeconds: number) => {
      nextIdRef.current += 1;
      const localId = `upload-${Date.now()}-${nextIdRef.current}`;
      pendingRef.current.set(localId, {
        purpose,
        target,
        scopeKey,
        blob,
        mimeType,
        durationSeconds,
        clipId: null,
        putDone: false,
      });
      setUploads((current) => [
        ...current,
        { scopeKey, upload: { localId, status: "uploading", progress: 0, createdAt: new Date().toISOString() } },
      ]);
      void runUpload(localId);
    },
    [purpose, target, scopeKey, runUpload],
  );

  const retry = useCallback(
    async (clipId: string) => {
      try {
        await voiceService.retryClip(http, clipId);
        markSubmitted(clipId, scopeKey);
      } catch {
        addToast({ type: "error", message: "Couldn't retry the voice note", duration: TOAST_MS });
      }
      await refresh(scopeKey);
    },
    [http, scopeKey, markSubmitted, refresh, addToast],
  );

  const discard = useCallback(
    async (clipId: string) => {
      try {
        await voiceService.discardClip(http, clipId);
      } catch {
        addToast({ type: "error", message: "Couldn't discard the voice note", duration: TOAST_MS });
      }
      await refresh(scopeKey);
    },
    [http, scopeKey, refresh, addToast],
  );

  /** Only upload_failed retries: upload_rejected is discard only, and uploading is busy. */
  const retryUpload = useCallback(
    (localId: string) => {
      const failed = uploads.some((entry) => entry.upload.localId === localId && entry.upload.status === "upload_failed");
      if (failed) void runUpload(localId);
    },
    [uploads, runUpload],
  );

  /**
   * A clip the server may already hold is discarded there too, so a submit
   * whose response was lost can't still produce a note. Best effort.
   */
  const discardUpload = useCallback(
    (localId: string) => {
      const clipId = pendingRef.current.get(localId)?.clipId ?? null;
      if (clipId !== null) void voiceService.discardClip(http, clipId).catch(() => undefined);
      removeUpload(localId);
    },
    [http, removeUpload],
  );

  // Any scope: leaving the page drops every upload in flight and every failed
  // upload's recording, which only lives in memory, shown or not.
  useBeforeUnloadWarning(
    uploads.some((entry) => entry.upload.status === "uploading" || entry.upload.status === "upload_failed"),
  );

  const clips: VoiceClipView[] = useMemo(
    () =>
      mergeClips(
        uploads.filter((entry) => entry.scopeKey === scopeKey).map((entry) => entry.upload),
        server,
      ),
    [uploads, scopeKey, server],
  );

  return { clips, send, retry, discard, retryUpload, discardUpload };
}
